import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    TextField,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    Chip,
    List,
    ListItem,
    IconButton,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    WifiOff as NetworkIcon,
    Security as SecurityIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import MarkingService from '../services/MarkingService';

const ChestnyZnakCodeChecker = () => {
    const { t } = useTranslation();
    const [codeInput, setCodeInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [checkingHistory, setCheckingHistory] = useState([]);
    const [apiStatus, setApiStatus] = useState(null);
    const [error, setError] = useState('');
    const [expandedError, setExpandedError] = useState(null); // ID развёрнутой ошибки в истории
    const [lastResult, setLastResult] = useState(null); // Последний результат для визуальной индикации
    const inputRef = useRef(null); // Ref для автофокуса поля ввода

    const checkAPIStatus = async () => {
        try {
            const status = await MarkingService.getChestnyZnakStatus();
            setApiStatus(status);
        } catch (error) {
            console.error('Ошибка проверки статуса API:', error);
            setApiStatus({ status: 'error', message: error.message });
        }
    };

    const handleCheckCode = useCallback(async () => {
        const code = codeInput.trim();
        if (!code) {
            setError(t('chestnyZnak.enterCode'));
            return;
        }

        setIsLoading(true);
        setError('');
        setLastResult(null); // Сбрасываем предыдущий результат

        try {
            const result = await MarkingService.quickCodeCheck(code);

            // Сохраняем последний результат для визуальной индикации
            setLastResult(result);

            // Добавляем результат в историю
            const historyItem = {
                id: Date.now(),
                code: code,
                timestamp: new Date(), // Всегда объект Date
                ...result
            };

            // Если в результате есть timestamp строка, оставляем как есть для детальной информации,
            // но для отображения в истории используем наш timestamp
            if (result.timestamp && typeof result.timestamp === 'string') {
                historyItem.serverTimestamp = result.timestamp;
            }

            setCheckingHistory(prev => [historyItem, ...prev.slice(0, 9)]); // Храним последние 10 проверок
            setCodeInput('');

            // Сразу устанавливаем фокус обратно на поле ввода для следующего сканирования
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);

        } catch (error) {
            console.error('Ошибка проверки кода:', error);
            setError(error.message || t('chestnyZnak.checkFailed'));

            // Сохраняем ошибку как последний результат для индикации
            setLastResult({
                source: 'error',
                valid: false,
                message: error.message || t('chestnyZnak.errorOccurred')
            });

            // Все равно возвращаем фокус на поле ввода
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        } finally {
            setIsLoading(false);
        }
    }, [codeInput, t]);

    // Загрузка статуса API и автофокус при монтировании компонента
    useEffect(() => {
        checkAPIStatus();
        // Устанавливаем фокус на поле ввода при монтировании
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, []);

    // Обработка сканирования штрихкода
    useEffect(() => {
        async function handleKeyDown(e) {
            if (e.key === 'Enter' && codeInput.trim().length > 0) {
                e.preventDefault();
                await handleCheckCode();
            }

            // Накопление символов (исключаем служебные клавиши)
            if (e.key !== 'Enter' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                setCodeInput(prev => prev + e.key);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [codeInput, handleCheckCode]);

    const handleClearHistory = () => {
        setCheckingHistory([]);
        setExpandedError(null);
        setLastResult(null); // Очищаем последний результат
    };

    // Повторная проверка кода
    const handleRetryCheck = async (code) => {
        setCodeInput(code);
        await handleCheckCode();
    };

    // Переключение развёрнутого состояния ошибки
    const toggleErrorExpansion = (itemId) => {
        setExpandedError(expandedError === itemId ? null : itemId);
    };

    // Получение иконки для типа ошибки
    const getErrorTypeIcon = (errorType, errorCode) => {
        switch (errorType) {
            case 'network':
                return <NetworkIcon color="error" />;
            case 'api':
                if (errorCode === 'UNAUTHORIZED') return <SecurityIcon color="warning" />;
                return <ErrorIcon color="error" />;
            default:
                return <WarningIcon color="error" />;
        }
    };

    // Получение цвета для чипа ошибки
    const getErrorChipColor = (item) => {
        if (item.source === 'local' && item.used) return 'warning';
        if (item.source === 'error') {
            if (item.requiresReauth) return 'warning';
            if (item.isRetryable) return 'info';
            return 'error';
        }
        if (item.source === 'chestnyznak') {
            if (item.valid) {
                if (item.isOwnedByFLX) return 'success'; // Зеленый для FLX
                if (item.isAvailableForSale) return 'primary'; // Синий для доступных но не FLX
                return 'warning'; // Желтый для действительных но недоступных
            }
            if (item.errorCode === '404') return 'error'; // Красный для 404
            return 'default';
        }
        return 'default';
    };

    // Получение текста для чипа
    const getChipLabel = (item) => {
        if (item.source === 'local' && item.used) return t('chestnyZnak.inUse', { message: item.message });
        if (item.source === 'chestnyznak') {
            if (item.valid) {
                if (item.isOwnedByFLX) return t('chestnyZnak.valid');
                if (item.isAvailableForSale) return t('chestnyZnak.valid');
                return t('chestnyZnak.unavailable');
            }
            if (item.errorCode === '404') return t('chestnyZnak.notFound');
            return t('chestnyZnak.invalid');
        }
        if (item.source === 'error') {
            if (item.requiresReauth) return t('chestnyZnak.reauthRequired');
            if (item.isRetryable) return t('chestnyZnak.canRetry');
            return t('chestnyZnak.error', { code: item.errorCode || 'Unknown' });
        }
        return item.message || 'Unknown';
    };

    const getStatusIcon = (source, valid, used, isAvailableForSale) => {
        if (source === 'error') return <ErrorIcon color="error" />;
        if (used) return <ErrorIcon color="warning" />;
        if (valid) {
            // Предупреждающая иконка для действительных но недоступных кодов
            if (!isAvailableForSale) return <WarningIcon sx={{ color: '#ed6c02' }} />; // оранжевый
            return <CheckIcon color="success" />;
        }
        return <ErrorIcon color="error" />;
    };


    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                {t('chestnyZnak.title')}
            </Typography>

            {/* Статус API */}
            {apiStatus && (
                <Alert
                    severity={apiStatus.status === 'available' ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                >
                    {t('chestnyZnak.apiStatus')} {apiStatus.status === 'available' ? t('chestnyZnak.apiAvailable') : t('chestnyZnak.apiUnavailable')}
                </Alert>
            )}

            {/* Форма проверки */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('chestnyZnak.checkCode')}
                    </Typography>

                    {/* Визуальная индикация последнего результата */}
                    {lastResult && (
                        <Box sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: lastResult.valid ?
                                    (lastResult.isOwnedByFLX ? '#e8f5e9' :
                                     lastResult.isAvailableForSale ? '#fff8e1' : '#fff8e1') :
                                    (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#ffebee' : 'warning.light',
                            border: 2,
                            borderColor: lastResult.valid ?
                                       (lastResult.isOwnedByFLX ? 'success.main' :
                                        lastResult.isAvailableForSale ? '#ffb74d' : '#ffb74d') :
                                       (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#ef5350' : 'warning.main'
                        }}>
                            <Typography variant="h6" sx={{
                                color: lastResult.valid ?
                                       (lastResult.isOwnedByFLX ? 'success.dark' :
                                        lastResult.isAvailableForSale ? '#f57f17' : '#f57f17') :
                                       (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#c62828' : 'warning.dark',
                                textAlign: 'center',
                                fontWeight: 'bold'
                            }}>
                                {lastResult.valid ?
                                    (lastResult.isOwnedByFLX ? t('chestnyZnak.codeValidFLX') :
                                     lastResult.isAvailableForSale ? t('chestnyZnak.codeValid') : t('chestnyZnak.codeValidUnavailable')) :
                                    lastResult.source === 'error' ? t('chestnyZnak.checkError') : t('chestnyZnak.codeInvalid')}
                            </Typography>
                            {lastResult.message && !(lastResult.valid && !lastResult.isAvailableForSale) && (
                                <Typography variant="body2" sx={{
                                    color: lastResult.valid ?
                                           (lastResult.isOwnedByFLX ? 'success.main' :
                                            lastResult.isAvailableForSale ? '#ef6c00' : 'warning.main') :
                                           (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#c62828' : 'warning.main',
                                    textAlign: 'center',
                                    mt: 1
                                }}>
                                    {lastResult.message}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            inputRef={inputRef}
                            label={t('chestnyZnak.codeLabel')}
                            value={codeInput}
                            onChange={(e) => setCodeInput(e.target.value)}
                            placeholder={t('chestnyZnak.codePlaceholder')}
                            disabled={isLoading}
                            sx={{
                                flexGrow: 1,
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: lastResult?.valid ?
                                                   (lastResult?.isOwnedByFLX ? 'success.main' :
                                                    lastResult?.isAvailableForSale ? 'primary.main' : 'warning.main') :
                                                   lastResult?.source === 'error' ? 'error.main' : 'warning.main',
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                            onClick={handleCheckCode}
                            disabled={isLoading || !codeInput.trim()}
                            sx={{
                                minWidth: 120,
                                bgcolor: lastResult?.valid ?
                                           (lastResult?.isOwnedByFLX ? 'success.main' :
                                            lastResult?.isAvailableForSale ? 'primary.main' : 'warning.main') :
                                           lastResult?.source === 'error' ? 'error.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: lastResult?.valid ?
                                               (lastResult?.isOwnedByFLX ? 'success.dark' :
                                                lastResult?.isAvailableForSale ? 'primary.dark' : 'warning.dark') :
                                               lastResult?.source === 'error' ? 'error.dark' : 'primary.dark'
                                }
                            }}
                        >
                            {isLoading ? t('chestnyZnak.checking') : t('chestnyZnak.checkCode')}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="caption" color="textSecondary">
                        {t('chestnyZnak.useScanner')}
                    </Typography>
                </CardContent>
            </Card>

            {/* История проверок */}
            {checkingHistory.length > 0 && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {t('chestnyZnak.checkHistory')}
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleClearHistory}
                                startIcon={<DeleteIcon />}
                            >
                                {t('chestnyZnak.clear')}
                            </Button>
                        </Box>

                        <List>
                            {checkingHistory.map((item) => (
                                <Box key={item.id}>
                                    <ListItem divider>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            {item.source === 'error' ?
                                                getErrorTypeIcon(item.errorType, item.errorCode) :
                                                getStatusIcon(item.source, item.valid, item.used, item.isAvailableForSale)
                                            }

                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {item.code}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {item.timestamp ?
                                                        (typeof item.timestamp === 'string' ?
                                                            new Date(item.timestamp).toLocaleTimeString() :
                                                            item.timestamp.toLocaleTimeString()
                                                        ) :
                                                        new Date().toLocaleTimeString()
                                                    }
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={getChipLabel(item)}
                                                    color={getErrorChipColor(item)}
                                                    size="small"
                                                    sx={{ maxWidth: 250 }}
                                                />

                                                {/* Кнопки действий для ошибок и детальной информации */}
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {item.isRetryable && (
                                                        <Tooltip title={t('chestnyZnak.retryCheck')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRetryCheck(item.code)}
                                                                color="primary"
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Показываем кнопку подробностей для всех случаев с детальной информацией */}
                                                    {(item.details || item.cisError || item.cisInfo || (item.details && item.source === 'chestnyznak')) && (
                                                        <Tooltip title={item.valid ? t('chestnyZnak.codeDetails') : t('chestnyZnak.details')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleErrorExpansion(item.id)}
                                                                color="info"
                                                            >
                                                                {expandedError === item.id ?
                                                                    <ExpandLessIcon fontSize="small" /> :
                                                                    <ExpandMoreIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItem>

                                    {/* Развёрнутые детали для всех типов результатов */}
                                    {expandedError === item.id && (
                                        <Collapse in={expandedError === item.id}>
                                            <Box sx={{ pl: 6, pr: 2, pb: 2, bgcolor: 'grey.50' }}>
                                                {/* Информация о коде для валидных результатов */}
                                                {(item.valid || item.cisInfo) && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                                            {t('chestnyZnak.codeInfo')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                                            • {t('chestnyZnak.gtin')}: {item.cisInfo?.gtin || t('chestnyZnak.notSpecified')}<br/>
                                                            • {t('chestnyZnak.productName')}: {item.cisInfo?.productName || t('chestnyZnak.notSpecified')}<br/>
                                                            • {t('chestnyZnak.brand')}: {item.cisInfo?.brand || t('chestnyZnak.notSpecified')}<br/>
                                                            • {t('chestnyZnak.owner')}: {item.cisInfo?.ownerName || t('chestnyZnak.notSpecified')}<br/>
                                                            • {t('chestnyZnak.ownerInn')}: {item.cisInfo?.ownerInn || t('chestnyZnak.notSpecified')}
                                                        </Typography>
                                                        {item.cisInfo?.status && (
                                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                                                • {t('chestnyZnak.statusCode')}: {item.cisInfo.status.russianName || item.cisInfo.status.description}<br/>
                                                                • {t('chestnyZnak.statusDescription')}: {item.cisInfo.status.description}<br/>
                                                                • {t('chestnyZnak.availableForSale')}: {item.isAvailableForSale ? t('chestnyZnak.yes') : t('chestnyZnak.no')}<br/>
                                                                • {t('chestnyZnak.ownedByFLX')}: {item.isOwnedByFLX ? t('chestnyZnak.yes') : t('chestnyZnak.no')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}

                                                {/* Ошибки кода маркировки */}
                                                {item.cisError && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" color="error" gutterBottom>
                                                            {t('chestnyZnak.codeError')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                                            • {t('chestnyZnak.errorCode')}: {item.cisError.code}<br/>
                                                            • {t('chestnyZnak.errorMessage')}: {item.cisError.message}<br/>
                                                            • {t('chestnyZnak.errorDescription')}: {item.cisError.russianDescription}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Информация об ошибках API */}
                                                {item.errorCode && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('chestnyZnak.errorCode')}:</strong> {item.errorCode}
                                                    </Typography>
                                                )}

                                                {item.errorType && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('chestnyZnak.errorType')}:</strong> {item.errorType}
                                                    </Typography>
                                                )}


                                                {/* Время проверки */}
                                                {(item.serverTimestamp || item.timestamp) && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>{t('chestnyZnak.checkTime')}:</strong> {
                                                            item.serverTimestamp ?
                                                                new Date(item.serverTimestamp).toLocaleString() :
                                                                (
                                                                    typeof item.timestamp === 'string' ?
                                                                        new Date(item.timestamp).toLocaleString() :
                                                                        item.timestamp.toLocaleString()
                                                                )
                                                        }
                                                    </Typography>
                                                )}

                                                {/* Предупреждения и рекомендации */}
                                                {item.requiresReauth && (
                                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                                        {t('chestnyZnak.reauthNeeded')}
                                                    </Alert>
                                                )}

                                                {item.isRetryable && (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        {t('chestnyZnak.retryCanFix')}
                                                    </Alert>
                                                )}

                                                {item.source === 'chestnyznak' && item.errorCode === '404' && (
                                                    <Alert severity="error" sx={{ mt: 1 }}>
                                                        {t('chestnyZnak.codeNotFound')}
                                                    </Alert>
                                                )}

                                                {item.valid && (
                                                    <Alert severity={item.isOwnedByFLX ? "success" : item.isAvailableForSale ? "info" : "warning"} sx={{ mt: 1 }}>
                                                        {item.isOwnedByFLX ? t('chestnyZnak.codeValidBelongsFLX') :
                                                         item.isAvailableForSale ? t('chestnyZnak.codeValidForSale') :
                                                         t('chestnyZnak.codeValidNotForSale')}
                                                    </Alert>
                                                )}

                                                {!item.valid && item.isOwnedByFLX && (
                                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                                        {t('chestnyZnak.codeOwnedByFLXNotForSale')}
                                                    </Alert>
                                                )}

                                                {!item.valid && !item.isOwnedByFLX && item.cisInfo && item.errorCode !== '404' && (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        {t('chestnyZnak.codeNotOwnedNotForSale')}
                                                    </Alert>
                                                )}
                                            </Box>
                                        </Collapse>
                                    )}
                                </Box>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            )}

            {/* Информация */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon />
                        {t('chestnyZnak.management')}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={checkAPIStatus}
                        sx={{ mt: 1 }}
                    >
                        {t('chestnyZnak.refreshApiStatus')}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ChestnyZnakCodeChecker;
